import { useState, useMemo, useRef } from "react";
import { Palette, Ruler, Tag, Lightbulb, Truck } from "lucide-react";

import { 
  createIntFormatter, 
  createPercentFormatter, 
  createDecimalFormatter, 
  currencyFormatter 
} from "../../../utils/formatter/formatter";

import { SYSTEM_COLORS } from "../../../components/system.colors";

// TODO: vir da API quando o endpoint estiver disponível
const IDEAL_COVERAGE = 2

const intFmt = createIntFormatter();
const pctFmt = createPercentFormatter();
const pct0Fmt = createPercentFormatter({ minimumFractionDigits: 0, maximumFractionDigits: 0 });
const decFmt = createDecimalFormatter({ minimumFractionDigits: 1, maximumFractionDigits: 1 });
const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const SUPPLIER_WINDOW_MONTHS = 12;

const TABS = {
  cor: {
    label: "Cor", icon: Palette, dataKey: "colorData",
    columns: [
      { key: "color", label: "Cor", align: "left" },
      { key: "inventory", label: "Estoque (Q)", fmt: intFmt },
      { key: "sales", label: "Vendas (Q)", fmt: intFmt },
      { key: "coverage", label: "Cob. últ. 30D", fmt: decFmt, noTotal: true },
      { key: "portfolio", label: "Carteira (Q)", fmt: intFmt },
      { key: "inventoryShare", label: "% Estoque", fmt: pctFmt, shareOf: "inventory" },
      { key: "portfolioShare", label: "% Carteira", fmt: pctFmt, shareOf: "portfolio" },
      { key: "salesShare", label: "% Vendas", fmt: pctFmt, shareOf: "sales" },
      { key: "recommendation", label: "Recomendação", fmt: pctFmt },
    ],
  },
  tamanho: {
    label: "Tamanho", icon: Ruler, dataKey: "sizeData", highlightCoverage: true,
    columns: [
      { key: "sizeName", label: "Tamanho", align: "left" },
      { key: "inventory", label: "Estoque (Q)", fmt: intFmt },
      { key: "sales", label: "Vendas (Q)", fmt: intFmt },
      { key: "coverage", label: "Cob. últ. 30D", fmt: decFmt, noTotal: true },
      { key: "portfolio", label: "Carteira (Q)", fmt: intFmt },
      { key: "commitment", label: "Compromisso", fmt: intFmt },
      { key: "inventoryShare", label: "% Estoque", fmt: pctFmt, shareOf: "inventory" },
      { key: "recommendation", label: "Recomendação", fmt: pctFmt },
    ],
  },
  preco: { label: "Preço", icon: Tag, dataKey: "priceData" },
  fornecedor: { label: "Fornecedor", icon: Truck },
};

export const buildSupplierColumns = () => {
  const now = new Date();

  return Array.from({ length: SUPPLIER_WINDOW_MONTHS }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() + index, 1);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    return {
      key: `${year}-${month}`,
      label: `${MONTHS[month - 1].toUpperCase()}/${String(year).slice(-2)}`,
    };
  });
};

export const buildSupplierRows = (supplierData, columns) => (supplierData || [])
  .map(supplier => ({
    brandName: supplier.brandName,
    valores: supplier.valores,
    total: columns.reduce((sum, column) => sum + (supplier.valores[column.key] || 0), 0),
  }))
  .filter(supplier => supplier.total > 0)
  .sort((a, b) => b.total - a.total);

const cardStyle = { 
  background: SYSTEM_COLORS.WHITE.WHITE, 
  border: `1px solid ${SYSTEM_COLORS.GRAY.MIST}`, 
  borderRadius: "12px", 
  overflow: "hidden" 
};

const cardHeaderStyle = {
  padding: "12px 20px", background: SYSTEM_COLORS.WHITE.BLUSH, borderBottom: `1px solid ${SYSTEM_COLORS.WHITE.SHELL}`,
  fontSize: "14px", fontWeight: "700", color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
};

const RecomendacaoTable = ({ ctx, uiState }) => {

  const { rows } = ctx || {};
  const { columns, highlightCoverage } = uiState || {};

  const sortedRows = useMemo(() => {
    const list = (rows || []).map(row => ({ ...row }));

    columns.filter(col => col.shareOf).forEach(col => {
      const total = list.reduce((sum, r) => sum + (Number(r[col.shareOf]) || 0), 0);
      list.forEach(r => { r[col.key] = total ? (Number(r[col.shareOf]) || 0) / total : 0; });
    });

    return list.sort((a, b) => (Number(b.recommendation) || 0) - (Number(a.recommendation) || 0));
  }, [rows, columns]);

  const maxRecommendation = Math.max(...sortedRows.map(r => Number(r.recommendation) || 0), 0.0001);

  const totals = useMemo(() => {
    const acc = {};
    columns.forEach(col => {
      if (col.fmt && !col.noTotal) acc[col.key] = sortedRows.reduce((sum, r) => sum + (Number(r[col.key]) || 0), 0);
    });
    return acc;
  }, [sortedRows, columns]);

  const isRec = (col) => col.key === "recommendation";

  const th = (col) => ({
    padding: "12px 14px", fontSize: "11.5px", fontWeight: "600", letterSpacing: "0.4px",
    textTransform: "uppercase", whiteSpace: "nowrap", textAlign: col.align || "right",
    color: isRec(col) ? SYSTEM_COLORS.ORANGE.BURNT_ORANGE : SYSTEM_COLORS.GRAY.DAVY_GRAY,
    background: isRec(col) ? SYSTEM_COLORS.ORANGE.PEACH_CREAM : SYSTEM_COLORS.WHITE.PORCELAIN,
    borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.ALTO}`,
  });

  const td = (col) => ({
    padding: "9px 14px", fontSize: "13px", whiteSpace: "nowrap", textAlign: col.align || "right",
    color: col.align === "left" ? SYSTEM_COLORS.BLACK.EERIE_BLACK : SYSTEM_COLORS.GRAY.DAVY_GRAY,
    fontWeight: col.align === "left" ? "600" : "400",
    borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.CULTURED}`,
  });

  const cellStyle = (col, row) => {
    const value = Number(row[col.key]) || 0;

    if (isRec(col)) {
      const alpha = 0.08 + ((value || 0) / maxRecommendation) * 0.52;
      return { ...td(col), fontWeight: "700", color: SYSTEM_COLORS.BLACK.EERIE_BLACK, background: `rgba(232,119,58,${alpha})` };
    }

    if (col.key === "coverage" && highlightCoverage) {
      return { ...td(col), color: value < IDEAL_COVERAGE ? SYSTEM_COLORS.RED.CARDINAL : SYSTEM_COLORS.GRAY.GREY };
    }

    return td(col);
  };

  const tfootStyle = (col) => ({
    ...td(col), color: SYSTEM_COLORS.BLACK.EERIE_BLACK, fontWeight: col.align === "left" ? "700" : "600",
    background: isRec(col) ? SYSTEM_COLORS.ORANGE.APRICOT_MIST : SYSTEM_COLORS.WHITE.SNOW, borderBottom: "none",
  });

  return (
    <div style={{ border: `1px solid ${SYSTEM_COLORS.GRAY.MIST}`, borderRadius: "10px", overflow: "auto" }}>
      <table style={{ borderCollapse: "collapse", inlineSize: "100%" }}>
        <thead>
          <tr>
            {columns.map(col => <th key={col.key} style={th(col)}>{col.label}</th>)}
          </tr>
        </thead>

        <tbody>
          {sortedRows.map((row, i) => (
            <tr key={row[columns[0].key] ?? i}>
              {columns.map(col => (
                <td key={col.key} style={cellStyle(col, row)}>
                  {col.fmt ? col.fmt.format(Number(row[col.key]) || 0) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>

        <tfoot>
          <tr>
            {columns.map((col, i) => (
              <td key={col.key} style={tfootStyle(col)}>
                {i === 0 ? "Total" : col.noTotal ? "—" : col.fmt.format(totals[col.key])}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>

      {highlightCoverage && (
        <p style={{ margin: 0, padding: "10px 14px", fontSize: "12px", color: SYSTEM_COLORS.GRAY.TAUPE_GRAY, borderTop: `1px solid ${SYSTEM_COLORS.GRAY.CULTURED}` }}>
          Recomendação proporcional à distância da cobertura atual em relação à cobertura ideal de{" "}
          <strong style={{ color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>{decFmt.format(IDEAL_COVERAGE)}</strong>.
        </p>
      )}
    </div>
  );
};

const PrecoView = ({ ctx }) => {

  const { priceData } = ctx || {};

  const { elasticity, action } = priceData?.[0] || {};

  const tiers = useMemo(() => {
    const row = priceData?.[0] || {};

    return [1, 2, 3, 4, 5]
      .filter(n => row[`p${n}`] != null)
      .map(n => ({ priceTier: `P${n}`, price: Number(row[`p${n}`]) || 0, share: Number(row[`p${n}Percentage`]) || 0 }))
      .reverse();
  }, [priceData]);

  const totalShare = tiers.reduce((sum, t) => sum + (t.share || 0), 0);
  const weightedPrice = totalShare ? tiers.reduce((sum, t) => sum + t.price * t.share, 0) / totalShare : 0;
  const maxShare = Math.max(...tiers.map(t => t.share || 0), 0.0001);

  const insightStyle = (bg, border) => ({
    display: "flex", alignItems: "center", gap: 16, padding: "14px 20px",
    background: bg, border: `1px solid ${border}`, borderRadius: "10px",
  });

  const iconBoxStyle = (bg) => ({
    inlineSize: 40, blockSize: 40, borderRadius: "8px", background: bg, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <section style={cardStyle}>
          <header style={cardHeaderStyle}>
            Pirâmide de Preço
            <p style={{ margin: "2px 0 0", fontSize: "12.5px", fontWeight: "400", color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
              Preço médio ponderado: <strong style={{ color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>{currencyFormatter.format(weightedPrice)}</strong>
            </p>
          </header>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "16px 20px" }}>
            {tiers.map(t => (
              <div key={t.priceTier} style={{ display: "flex", alignItems: "center", gap: 24 }}>
                <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                  <div style={{
                    inlineSize: `${40 + (t.share / maxShare) * 60}%`, padding: "6px 0", textAlign: "center",
                    background: SYSTEM_COLORS.ORANGE.PEACH_FUZZ, border: `1px solid ${SYSTEM_COLORS.ORANGE.MELON}`, borderRadius: "8px",
                  }}>
                    <div style={{ fontSize: "13.5px", fontWeight: "700", color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>{t.priceTier}</div>
                    <div style={{ fontSize: "11.5px", color: SYSTEM_COLORS.GRAY.DIM_GRAY }}>{currencyFormatter.format(t.price)}</div>
                  </div>
                </div>

                <span style={{ inlineSize: 60, fontSize: "17px", fontWeight: "700", color: SYSTEM_COLORS.ORANGE.BURNT_ORANGE }}>
                  {pct0Fmt.format(t.share)}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section style={{ ...cardStyle, display: "flex", flexDirection: "column" }}>
          <header style={cardHeaderStyle}>Elasticidade de preço da Categoria</header>

          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px", textAlign: "center" }}>
            <span style={{ fontSize: "42px", fontWeight: "800", color: SYSTEM_COLORS.ORANGE.BURNT_ORANGE }}>
              {elasticity != null ? pct0Fmt.format(Number(elasticity)) : "—"}
            </span>
            <p style={{ margin: "4px 0 0", fontSize: "13px", color: SYSTEM_COLORS.GRAY.DIM_GRAY }}>
              Variação de demanda para cada 10% de variação de preço
            </p>
          </div>
        </section>
      </div>

      <section style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 16, padding: 20 }}>
        <div style={insightStyle(SYSTEM_COLORS.GREEN.HONEYDEW, SYSTEM_COLORS.GREEN.MINT)}>
          <div style={iconBoxStyle(SYSTEM_COLORS.GREEN.MINT_LIGHT)}><Lightbulb size={20} color={SYSTEM_COLORS.GREEN.JADE} /></div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: "600", color: SYSTEM_COLORS.GREEN.JADE, letterSpacing: "0.4px" }}>AÇÃO RECOMENDADA</div>
            <div style={{ fontSize: "15px", fontWeight: "600", color: SYSTEM_COLORS.GREEN.DARK_FOREST, marginTop: 2 }}>{action || "—"}</div>
          </div>
        </div>
      </section>
    </div>
  );
};

const TOTAL_TINT = `linear-gradient(${SYSTEM_COLORS.ORANGE.BURNT_ORANGE_12}, ${SYSTEM_COLORS.ORANGE.BURNT_ORANGE_12})`;

const supplierTh = (isFirst, isTotal) => ({
  padding: "12px 14px", fontSize: "11.5px", fontWeight: "600", letterSpacing: "0.4px",
  textTransform: "uppercase", whiteSpace: "nowrap", textAlign: isFirst ? "left" : "right",
  color: isTotal ? SYSTEM_COLORS.ORANGE.CINNABAR : SYSTEM_COLORS.BLUE.SLATE,
  backgroundColor: SYSTEM_COLORS.GRAY.CLOUD_GRAY,
  ...(isTotal ? { backgroundImage: TOTAL_TINT } : {}),
  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PEARL_GRAY}`,
  ...(isFirst ? { position: "sticky", left: 0, zIndex: 1, minInlineSize: 150 } : {}),
});

const supplierTd = (isFirst, isTotal, isFooter, isStriped) => ({
  padding: "9px 14px", fontSize: "13px", whiteSpace: "nowrap", textAlign: isFirst ? "left" : "right",
  color: isFooter && isTotal ? SYSTEM_COLORS.ORANGE.CINNABAR : SYSTEM_COLORS.BLACK.GUNMETAL,
  fontWeight: isFirst || isTotal || isFooter ? "700" : "400",
  backgroundColor: isFirst
    ? SYSTEM_COLORS.WHITE.WHITE
    : isFooter
      ? SYSTEM_COLORS.GRAY.CLOUD_GRAY
      : isStriped ? SYSTEM_COLORS.GRAY.GRAY_50 : SYSTEM_COLORS.WHITE.WHITE,
  ...(isTotal ? { backgroundImage: TOTAL_TINT } : {}),
  borderTop: isFooter ? `2px solid ${SYSTEM_COLORS.GRAY.PEARL_GRAY}` : "none",
  borderBottom: isFooter ? "none" : `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
  ...(isFirst ? { position: "sticky", left: 0, zIndex: 1 } : {}),
});

const FornecedorView = ({ ctx }) => {

  const { columns, rows } = ctx || {};

  const columnTotals = useMemo(
    () => columns.map(column => rows.reduce((sum, row) => sum + (row.valores[column.key] || 0), 0)),
    [columns, rows]
  );

  const grandTotal = columnTotals.reduce((sum, value) => sum + value, 0);

  const renderValue = (value) => (value ? intFmt.format(value) : "—");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Truck size={16} color={SYSTEM_COLORS.ORANGE.BURNT_ORANGE} />
          <span style={{ fontSize: "15px", fontWeight: "700", color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>Carteira de Pedidos Atual</span>
        </div>

        <span style={{ fontSize: "12.5px", color: SYSTEM_COLORS.GRAY.TAUPE_GRAY }}>Pedidos pendentes · peças</span>
      </div>

      <div style={{ border: `1px solid ${SYSTEM_COLORS.GRAY.PEARL_GRAY}`, borderRadius: "10px", overflow: "auto" }}>
        <table style={{ borderCollapse: "separate", borderSpacing: 0, inlineSize: "100%" }}>
          <thead>
            <tr>
              <th style={supplierTh(true, false)}>FORNECEDOR</th>
              {columns.map(column => (
                <th key={column.key} style={supplierTh(false, false)}>{column.label}</th>
              ))}
              <th style={supplierTh(false, true)}>TOTAL</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, rowIndex) => {
              const isStriped = rowIndex % 2 === 1;

              return (
                <tr key={row.brandName}>
                  <td style={supplierTd(true, false, false, isStriped)}>{row.brandName}</td>

                  {columns.map(column => (
                    <td
                      key={column.key}
                      style={{
                        ...supplierTd(false, false, false, isStriped),
                        color: row.valores[column.key] ? SYSTEM_COLORS.BLACK.GUNMETAL : SYSTEM_COLORS.GRAY.NEUTRAL_GRAY,
                      }}
                    >
                      {renderValue(row.valores[column.key])}
                    </td>
                  ))}

                  <td style={supplierTd(false, true, false, isStriped)}>{intFmt.format(row.total)}</td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr>
              <td style={{ ...supplierTd(false, false, true), textAlign: "left" }}>Total</td>

              {columns.map((column, index) => (
                <td key={column.key} style={supplierTd(false, false, true)}>
                  {renderValue(columnTotals[index])}
                </td>
              ))}

              <td style={supplierTd(false, true, true)}>{intFmt.format(grandTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export function PlanoDeCompraRecomendacoes({ ctx, uiState }) {

  const { detailedData, supplierData } = ctx || {};
  const { isLoading } = uiState || {};

  const [selectedTab, setSelectedTab] = useState(null);
  const initialTabRef = useRef(null);

  const supplierColumns = useMemo(buildSupplierColumns, []);
  const supplierRows = useMemo(() => buildSupplierRows(supplierData, supplierColumns), [supplierData, supplierColumns]);

  const availability = {
    cor: !isLoading && Boolean(detailedData?.colorData?.length),
    tamanho: !isLoading && Boolean(detailedData?.sizeData?.length),
    preco: !isLoading && Boolean(detailedData?.priceData?.length),
    fornecedor: supplierRows.length > 0,
  };

  const availableKeys = Object.keys(TABS).filter(key => availability[key]);

  if (!initialTabRef.current && availableKeys.length > 0) {
    initialTabRef.current = availableKeys[0];
  }

  const preferredKey = selectedTab ?? initialTabRef.current;
  const activeKey = preferredKey && availability[preferredKey] ? preferredKey : availableKeys[0] ?? null;
  const tab = activeKey ? TABS[activeKey] : null;

  return (
    <section style={{ ...cardStyle, maxInlineSize: 1160, border: `1px solid ${SYSTEM_COLORS.GRAY.PEARL_GRAY}` }}>
      <header style={{ 
        display: "flex", alignItems: "center", gap: 24, 
        padding: "16px 24px",
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
        borderBlockEnd: `1px solid ${SYSTEM_COLORS.GRAY.PEARL_GRAY}`,}}>
        <hgroup>
          <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "700", color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>Recomendações</h3>
          <p style={{ margin: "2px 0 0", fontSize: "13px", color: SYSTEM_COLORS.GRAY.DIM_GRAY }}>Detalhamento por cor, tamanho e faixa de preço</p>
        </hgroup>

        <div style={{ 
          display: "flex", 
          border: `1px solid ${SYSTEM_COLORS.GRAY.SILVER}`, 
          borderRadius: "8px", overflow: "hidden", 
          background: SYSTEM_COLORS.GRAY.STONE_100 }}>
          {Object.entries(TABS).map(([key, { label, icon: Icon }]) => {
            const active = key === activeKey;
            const enabled = availability[key];

            return (
              <button
                key={key}
                disabled={!enabled}
                onClick={() => setSelectedTab(key)}
                style={{
                  display: "flex", alignItems: "center", gap: 8, border: "none",
                  cursor: enabled ? "pointer" : "not-allowed", opacity: enabled ? 1 : 0.45,
                  padding: "8px 20px", fontSize: "14px", fontWeight: active ? "600" : "500",
                  background: active ? SYSTEM_COLORS.ORANGE.BURNT_ORANGE : "transparent",
                  color: active ? SYSTEM_COLORS.WHITE.WHITE : SYSTEM_COLORS.BLACK.JET,
                }}>
                <Icon size={16} />
                {label}
              </button>
            );
          })}
        </div>
      </header>

      <div style={{ padding: 20 }}>
        {!activeKey
          ? <p style={{ margin: 0, padding: "24px 0", textAlign: "center", fontSize: "13px", color: SYSTEM_COLORS.GRAY.GREY }}>
              {isLoading ? "Carregando recomendações..." : "Sem dados disponíveis"}
            </p>
          : activeKey === "fornecedor"
          ? <FornecedorView ctx={{ columns: supplierColumns, rows: supplierRows }} />
          : activeKey === "preco"
          ? <PrecoView ctx={{ priceData: detailedData?.priceData }} />
          : <RecomendacaoTable
              ctx={{ rows: detailedData?.[tab.dataKey] }}
              uiState={{ columns: tab.columns, highlightCoverage: tab.highlightCoverage }} />}
      </div>
    </section>
  );
}